import React from 'react';
import styled from 'styled-components';
import {
  brandColours,
  fontSize,
  maxBreakpointQuery,
  minBreakpointQuery,
  sectionMargins,
  standardColours,
} from '../styles';
import { Svg } from './ui';
import shareIcon from '../images/share-icon.inline.svg';
import linkedinIcon from '../images/linkedin-icon.inline.svg';
import twitterIcon from '../images/twitter-icon.inline.svg';
import facebookIcon from '../images/facebook-icon.inline.svg';
import whatsappIcon from '../images/whatsapp-icon.inline.svg';
import messengerIcon from '../images/messenger-icon.inline.svg';

const StyledSocialShare = styled.div`
  display: flex;
  justify-self: center;
  align-items: center;
  padding: 15px;
  background-color: ${standardColours.white};
  border: 1px solid ${standardColours.lighterGrey};
  border-radius: 70px;

  ${maxBreakpointQuery.xlarge`
    ${sectionMargins('20px', '40px')};
    margin-left: 30px;
    margin-right: 30px;
  `}
`;

const StyledText = styled.span`
  ${fontSize(12)};
  white-space: nowrap;
  text-transform: uppercase;

  ${maxBreakpointQuery.xlarge`
    margin-right: 15px;
  `}

  ${minBreakpointQuery.xlarge`
    margin-bottom: 15px;
  `}
`;

const StyledShareIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 32px;
  width: 32px;
  border: 1px solid ${brandColours.secondary};
  background-color: ${standardColours.white};
  border-radius: 50%;

  ${minBreakpointQuery.small`
    height: 45px;
    width: 45px;
  `}
`;

const StyledShareIcon = styled(Svg)`
  height: 22px;
  width: 22px;
  fill: ${brandColours.secondary};
`;

const StyledList = styled.ul`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  ${minBreakpointQuery.xlarge`
    flex-direction: column;
  `}
`;

const StyledItem = styled.li``;

const StyledLink = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 32px;
  width: 32px;
  border: 1px solid ${brandColours.secondary};
  background-color: ${standardColours.white};
  border-radius: 50%;

  ${minBreakpointQuery.small`
    height: 45px;
    width: 45px;
  `}
`;

const StyledSocialIcon = styled(Svg)`
  height: 16px;
  width: 16px;
  fill: ${brandColours.secondary};

  ${minBreakpointQuery.small`
    height: 22px;
    width: 22px;
  `}
`;

const SocialShare = ({ text, shareData, ...props }) => {
  const webShareAvailable =
    typeof window !== 'undefined' && navigator && navigator.share;

  const links = [
    {
      url: `https://twitter.com/intent/tweet?url=${shareData.url}&text=${shareData.title}`,
      text: 'Share on Twitter',
      icon: twitterIcon,
    },
    {
      url: `https://www.facebook.com/sharer/sharer.php?u=${shareData.url}`,
      text: 'Share on Facebook',
      icon: facebookIcon,
    },
    {
      url: `http://www.linkedin.com/shareArticle?mini=true&url=${shareData.url}&title=${shareData.title}`,
      text: 'Share on LinkedIn',
      icon: linkedinIcon,
    },
    {
      url: `https://wa.me/?text=${encodeURIComponent(
        `${shareData.title} ${shareData.url}`,
      )}`,
      text: 'Share on WhatsApp',
      icon: whatsappIcon,
    },
    {
      url: `http://www.facebook.com/dialog/send?&link=${encodeURIComponent(
        `${shareData.title} ${shareData.url}`,
      )}`,
      text: 'Share on Messenger',
      icon: messengerIcon,
    },
  ];

  return (
    <StyledSocialShare
      as={webShareAvailable && 'button'}
      onClick={() => webShareAvailable && navigator.share(shareData)}
      {...props}
    >
      <StyledText>{text}</StyledText>
      {webShareAvailable ? (
        <StyledShareIconWrapper>
          <StyledShareIcon image={shareIcon} />
        </StyledShareIconWrapper>
      ) : (
        <StyledList>
          {links.map(({ url, text, icon }, id) => (
            <StyledItem key={id}>
              <StyledLink
                href={url}
                rel="noopener noreferrer"
                title={text}
                aria-label={text}
              >
                <StyledSocialIcon image={icon} />
              </StyledLink>
            </StyledItem>
          ))}
        </StyledList>
      )}
    </StyledSocialShare>
  );
};

export default SocialShare;
